import React, { useState } from "react";

// Shared book search component used by both AddBook and ChildDetail.
// Props:
//   onSelect(book) — called when a book card is confirmed as the selection
//   selectedBook   — the currently selected book object (or null)
//   onClear()      — called when the user taps ✕ on the selected book

export default function BookSearchCarousel({ onSelect, selectedBook, onClear }) {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  async function searchBooks() {
    if (!query.trim()) return;
    setSearching(true);
    setSearchResults([]);
    setCarouselIndex(0);

    try {
      // Both APIs fire in parallel
      const [gbRes, olRes] = await Promise.all([
        fetch(
          `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(query)}&maxResults=10&key=AIzaSyD_tDS4rTBkjHp__ryDPFEYXbzGs2_CTPo`
        ),
        fetch(
          `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=10`
        )
      ]);

      const gbData = await gbRes.json();
      const olData = await olRes.json();

      // Parse Google Books
      const gbResults = (gbData.items || []).map((item) => {
        const info = item.volumeInfo;
        return {
          source: "GB",
          title: info.title,
          author: info.authors ? info.authors[0] : "Unknown Author",
          coverUrl: info.imageLinks
            ? info.imageLinks.thumbnail.replace("http://", "https://")
            : null,
          pages: info.pageCount || "",
          publisher: info.publisher || "",
          year: info.publishedDate ? info.publishedDate.substring(0, 4) : ""
        };
      });

      // Parse Open Library
      const olResults = (olData.docs || []).map((doc) => ({
        source: "OL",
        title: doc.title,
        author: doc.author_name ? doc.author_name[0] : "Unknown Author",
        coverUrl: doc.cover_i
          ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg`
          : null,
        pages: doc.number_of_pages_median || "",
        publisher: doc.publisher ? doc.publisher[0] : "",
        year: doc.first_publish_year ? String(doc.first_publish_year) : ""
      }));

      // Interleave so results alternate GB/OL rather than all of one then all of the other
      const combined = [];
      const maxLen = Math.max(gbResults.length, olResults.length);
      for (let i = 0; i < maxLen; i++) {
        if (gbResults[i]) combined.push(gbResults[i]);
        if (olResults[i]) combined.push(olResults[i]);
      }

      setSearchResults(combined);
    } catch {
      alert("Search failed. Please try again.");
    }
    setSearching(false);
  }

  function handleSelect(book) {
    onSelect(book);
    setQuery(book.title);
    setSearchResults([]);
  }

  const canGoPrev = carouselIndex > 0;
  const canGoNext = carouselIndex < searchResults.length - 1;

  return (
    <div>
      {/* Search input row */}
      <div style={styles.searchRow}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search by title or author..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && searchBooks()}
        />
        <button style={styles.searchBtn} onClick={searchBooks}>
          {searching ? "..." : "Search"}
        </button>
      </div>

      {/* Carousel results */}
      {searchResults.length > 0 && (
        <div>
          <p style={styles.resultsCount}>
            {searchResults.length} results — tap a book to select it
          </p>
          <div style={styles.carouselWrapper}>
            {/* Prev arrow */}
            <button
              style={{ ...styles.carouselArrow, opacity: canGoPrev ? 1 : 0.2 }}
              onClick={() => canGoPrev && setCarouselIndex((i) => i - 1)}
              disabled={!canGoPrev}
            >
              ‹
            </button>

            {/* Cards — window of 3 centered on carouselIndex */}
            <div style={styles.carouselTrack}>
              {searchResults
                .slice(
                  Math.max(0, carouselIndex - 1),
                  Math.max(0, carouselIndex - 1) + 3
                )
                .map((book, i) => {
                  const absoluteIndex = Math.max(0, carouselIndex - 1) + i;
                  const isCenter = absoluteIndex === carouselIndex;
                  return (
                    <div
                      key={absoluteIndex}
                      style={{
                        ...styles.carouselCard,
                        transform: isCenter ? "scale(1.05)" : "scale(0.92)",
                        opacity: isCenter ? 1 : 0.6,
                        boxShadow: isCenter
                          ? "0 6px 20px rgba(0,0,0,0.15)"
                          : "0 2px 6px rgba(0,0,0,0.08)",
                      }}
                      onClick={() => {
                        if (isCenter) {
                          handleSelect(book);
                        } else {
                          setCarouselIndex(absoluteIndex);
                        }
                      }}
                    >
                      <span style={{
                        ...styles.sourceBadge,
                        backgroundColor: book.source === "GB" ? "#4299e1" : "#e53e3e"
                      }}>
                        {book.source === "GB" ? "Google" : "Open Library"}
                      </span>
                      {book.coverUrl ? (
                        <img src={book.coverUrl} alt={book.title} style={styles.carouselCover} />
                      ) : (
                        <div style={styles.carouselNoCover}>📖</div>
                      )}
                      <p style={styles.carouselTitle} title={book.title}>
                        {book.title.length > 40 ? book.title.substring(0, 38) + "…" : book.title}
                      </p>
                      <p style={styles.carouselAuthor}>{book.author}</p>
                      {book.year && <p style={styles.carouselMeta}>{book.year}</p>}
                      {book.pages && <p style={styles.carouselPages}>{book.pages} pages</p>}
                      {isCenter && <p style={styles.carouselTapHint}>Tap to select</p>}
                    </div>
                  );
                })}
            </div>

            {/* Next arrow */}
            <button
              style={{ ...styles.carouselArrow, opacity: canGoNext ? 1 : 0.2 }}
              onClick={() => canGoNext && setCarouselIndex((i) => i + 1)}
              disabled={!canGoNext}
            >
              ›
            </button>
          </div>

          {/* Position dots */}
          <div style={styles.dotsRow}>
            {searchResults.map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.dot,
                  backgroundColor: i === carouselIndex ? "#4299e1" : "#e2e8f0",
                  width: i === carouselIndex ? 16 : 8,
                }}
                onClick={() => setCarouselIndex(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Selected book confirmation */}
      {selectedBook && (
        <div style={styles.selectedBook}>
          {selectedBook.coverUrl && (
            <img src={selectedBook.coverUrl} alt={selectedBook.title} style={styles.selectedCover} />
          )}
          <div style={{ flex: 1 }}>
            <p style={styles.selectedTitle}>{selectedBook.title}</p>
            <p style={styles.selectedAuthor}>by {selectedBook.author}</p>
            {selectedBook.publisher && (
              <p style={styles.selectedMeta}>Publisher: {selectedBook.publisher}</p>
            )}
            {selectedBook.year && (
              <p style={styles.selectedMeta}>Published: {selectedBook.year}</p>
            )}
            <span style={{
              ...styles.sourceBadge,
              backgroundColor: selectedBook.source === "GB" ? "#4299e1" : "#e53e3e",
              marginTop: 6,
              display: "inline-block"
            }}>
              {selectedBook.source === "GB" ? "Google Books" : "Open Library"}
            </span>
          </div>
          <button style={styles.clearBtn} onClick={onClear}>✕</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  searchRow: { display: "flex", gap: 8, marginBottom: 4 },
  searchInput: { flex: 1, padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 16, boxSizing: "border-box" },
  searchBtn: { padding: "12px 20px", backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", whiteSpace: "nowrap" },

  resultsCount: { fontSize: 13, color: "#718096", margin: "12px 0 8px 0" },

  carouselWrapper: { display: "flex", alignItems: "center", gap: 8, padding: "12px 0" },
  carouselArrow: { fontSize: 36, background: "none", border: "none", cursor: "pointer", color: "#4299e1", fontWeight: "bold", padding: "0 4px", lineHeight: 1, flexShrink: 0 },
  carouselTrack: { display: "flex", gap: 10, flex: 1, justifyContent: "center", alignItems: "stretch", overflow: "hidden" },
  carouselCard: { flex: "0 0 calc(33% - 8px)", backgroundColor: "white", borderRadius: 12, padding: 12, border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s ease", display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 },
  sourceBadge: { fontSize: 10, fontWeight: "bold", color: "white", borderRadius: 6, padding: "2px 6px", marginBottom: 8 },
  carouselCover: { width: 70, height: 105, objectFit: "cover", borderRadius: 6, marginBottom: 8 },
  carouselNoCover: { width: 70, height: 105, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f7fafc", borderRadius: 6, fontSize: 28, marginBottom: 8 },
  carouselTitle: { fontSize: 12, fontWeight: "bold", color: "#2d3748", margin: "0 0 4px 0", textAlign: "center", lineHeight: 1.3 },
  carouselAuthor: { fontSize: 11, color: "#718096", margin: "0 0 2px 0", textAlign: "center" },
  carouselMeta: { fontSize: 11, color: "#a0aec0", margin: "0 0 2px 0" },
  carouselPages: { fontSize: 11, color: "#48bb78", fontWeight: "bold", margin: "0 0 4px 0" },
  carouselTapHint: { fontSize: 10, color: "#4299e1", fontWeight: "bold", margin: "4px 0 0 0" },

  dotsRow: { display: "flex", justifyContent: "center", alignItems: "center", gap: 4, paddingBottom: 4 },
  dot: { height: 8, borderRadius: 4, cursor: "pointer", transition: "all 0.2s ease" },

  selectedBook: { display: "flex", gap: 16, padding: 16, backgroundColor: "#ebf8ff", borderRadius: 8, marginTop: 12, alignItems: "flex-start" },
  selectedCover: { width: 60, height: 90, objectFit: "cover", borderRadius: 6, flexShrink: 0 },
  selectedTitle: { fontWeight: "bold", color: "#2d3748", margin: "0 0 4px 0" },
  selectedAuthor: { color: "#718096", margin: "0 0 4px 0", fontSize: 14 },
  selectedMeta: { color: "#a0aec0", margin: "0 0 2px 0", fontSize: 12 },
  clearBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#a0aec0", fontWeight: "bold", padding: 0, flexShrink: 0 },
};